/* eslint-disable no-param-reassign */
/* eslint-disable default-case */
/* eslint-disable @typescript-eslint/no-unsafe-call */
// @ts-nocheck
// import * as HXKlineChart from '@ths-m/HXKlineChart';
// import * as HXKline from '@ths-m/HXKline';
import { type Layer, type HXKLineLayerSpec, defineHXKLineLayer } from '../../../core/layer';
import type { StockData, KLineData, ExtendLineSeries } from '../../../helper/hxKLine';
import { validateHXKlineComponents } from '../../../util/hxKLine';

validateHXKlineComponents();
HXKline.setHXKlineChart(HXKlineChart);

const visInfo: Layer['visInfo'] = {
  id: 'hxKLineAndDynamicLine',
  name_en: 'Candlestick Chart And Dynamic Line',
  // name_zh: '行情图叠加动态折线',
  types_en: [],
  purpose_en: [],
  purpose_zh: [],
  standers: '',
  parameters: {},
  extra: {
    isNotTrainedByLLM: true
  }
};

export interface Spec extends HXKLineLayerSpec {
  /** 折线图使用的数据集索引，默认为 0 */
  dataIndex?: number;
  /** 折线图数据编码 */
  encoding: {
    x: string;
    y: string[];
  };
  stocks: StockData[];
}

/** 暂不支持legend的点击功能 */
export const layer = defineHXKLineLayer<Spec>({
  type: visInfo.id,
  isIndependentLayer: true,
  visInfo,
  // eslint-disable-next-line max-lines-per-function, sonarjs/cognitive-complexity
  render(api, spec, $dom) {
    const { layout, token, data: dataArray, hook, util, ui } = api;
    let legendIns: InstanceType<(typeof ui)['Legend']['Legend']>;
    let kLineTooltipIns: InstanceType<(typeof ui)['KLineTooltip']>;
    let tooltipIns: InstanceType<(typeof ui)['Tooltip']>;
    let hxKLine: { destroy: () => void };
    let timelineIns: { destroy: () => void };
    let timelineDropdownIns: InstanceType<(typeof ui)['Dropdown']>;
    let klinePlayTimer: ReturnType<typeof setTimeout>;
    const colorList = token?.color || token['--color-visualization'];

    HXKline.verifyProjectPermission(hook.globalConfig(api).hxKLineVerifyInfo);

    // * -------------------------- dom
    const showTimeline = layout !== util.const.LAYOUT.SMALL;
    const showDropdownTimeline = !showTimeline;
    const timelineTheme =
      api.token?.dvTimeline?.theme ||
      (api.layout === api.util.const.LAYOUT.SMALL_2_1 ? 'pc-simple' : 'pc');

    const $toolbar = document.createElement('section');
    const $toolbarLegend = document.createElement('section');
    const $toolbarDropdownTimeline = document.createElement('section');
    const $tooltipContainer = document.createElement('section');
    const $chart = document.createElement('section');
    const $leftYAxis = document.createElement('section');
    const $kline = document.createElement('section');
    const $timeline = document.createElement('section');
    const lineColor = token?.dvHXKLine?.constants?.DEFAULT_TICK_LINE_COLOR;
    const borderLineStyle = `1px solid ${lineColor || 'rgba(125, 133, 146, 0.3)'}`;
    const axisTickColor = token?.dvHXKLine?.constants?.DEFAULT_FONT_COLOR;

    $dom.style.cssText +=
      'display: flex; flex-direction: column; justify-content: center; align-items: center;';

    $toolbar.style.cssText =
      'width: 100%; margin-bottom: 8px; display: flex; flex-direction: row; align-items: center; justify-content: flex-start;';
    $toolbarLegend.style.cssText = 'flex: 1; width: 0;';
    $toolbarDropdownTimeline.style.cssText = 'margin-left: auto;';
    $chart.style.cssText =
      'position: relative; flex: 1; width: 100%; height: 0; display: flex; flex-direction: row;';
    $leftYAxis.style.cssText = `
      cursor: default;
      font-family: Microsoft YaHei;
      width: fit-content;
      min-width: 20px;
      height: 100%;
      font-size: 12px;
      color: ${axisTickColor};
      overflow: hidden;
      text-align: right;
      padding-right: 6px;
      border-right: 1px solid ${borderLineStyle};
    `;
    $kline.style.cssText = `
      position: relative;
      flex: 1;
      width: 0;
      height; 100%;
    `;
    $kline.setAttribute('id', util.common.generateUID());

    $timeline.style.cssText = `width: 100%; margin-top: 8px; padding: 0; box-shadow: none; border-color: transparent; ${api.token?.dvTimeline?.domCSSText}`;

    $toolbar.appendChild($toolbarLegend);
    if (showDropdownTimeline) {
      $toolbar.appendChild($toolbarDropdownTimeline);
    }
    $chart.appendChild($leftYAxis);
    $chart.appendChild($kline);
    $dom.appendChild($toolbar);
    $dom.appendChild($tooltipContainer);
    $dom.appendChild($chart);
    if (showTimeline) {
      $dom.appendChild($timeline);
    }

    // * -------------------------- data
    const {
      dataIndex = 0,
      encoding: { x, y = [] },
      stocks
    } = spec;
    const data = dataArray[dataIndex];
    const dataMetaInfo = hook.parseDataMetaInfo(data);
    const transformData = util.dataTransform.transform(data.values.slice(), [
      { type: 'sort', field: x, order: 'ascending' },
      { type: 'deDuplication', field: x }
    ]) as (typeof data)['values'];
    const { x: xAxisData, ...yAxisDataMap } = util.dataEncoding.encoding(transformData, {
      x: { field: x },
      ...y.reduce(
        (map, field) => ({
          ...map,
          [field]: { field, transforms: [{ type: 'toNumeric' }] }
        }),
        {}
      )
    });
    const colorMap = y.reduce(
      (result, item, index) => ({
        ...result,
        [item]: colorList[index % (colorList.length || 0)] || 'blue'
      }),
      {}
    );
    let dateIndex = xAxisData.length - 1;
    const yAxisSplitNum = 5;
    const drawLines = {
      linesMap: {} as {
        [key: string]: { color: string; data: number[] };
      },
      yAxisKey: null,
      yAxisDataUnit: '',
      gap: 0.1
    };
    const hxKLineAndDynamicLineToken = util.data.deepMerge(
      {
        option: {
          yAxis: {
            splitNum: yAxisSplitNum
          }
        }
      },
      token?.dvHXKLine?.hxKLineAndDynamicLine ?? {},
      { override: true }
    );
    // --- 求极值
    const maxExtent = { extent: 0, key: y[0] };

    y.forEach(yField => {
      const extent = Math.max(...yAxisDataMap[yField]) - Math.min(...yAxisDataMap[yField]);

      if (maxExtent.extent < extent) {
        maxExtent.extent = extent;
        maxExtent.key = yField;
      }

      // 处理每个折线的数据
      drawLines.linesMap[yField] = { color: colorMap[yField], data: yAxisDataMap[yField] };
    });

    drawLines.yAxisKey = maxExtent.key;
    drawLines.yAxisDataUnit = dataMetaInfo[maxExtent.key]?.unit || '';

    // * -------------------------- timeline dropdown
    if (showDropdownTimeline) {
      timelineDropdownIns = new ui.Dropdown($toolbarDropdownTimeline, {
        data: xAxisData.map((date, index) => ({ index, key: date, text: date })),
        defaultValue: xAxisData[dateIndex],
        listMaxHeight: $dom.getBoundingClientRect().height * 0.75,
        token: api.token?.dvDropdown
      });
    }

    // * -------------------------- legend
    const selectedStockIndex = 0;
    let selectedStock = stocks[selectedStockIndex];
    const legendData = y.map(item => ({
      name: item,
      color: colorMap[item],
      type: util.const.SERIES_2_LEGEND_TYPE_MAP.line,
      selected: true,
      disableSelect: true
    }));
    legendData.push({
      type: ui.Legend.ShowType.KLine,
      stocks,
      defaultValueIndex: selectedStockIndex
    });
    legendIns = new ui.Legend.Legend($toolbarLegend, {
      data: legendData,
      layout: 'pagination',
      token
    });

    // * -------------------------- tooltip
    function getTooltipData(tooltipArray: string[], layout_: string, LAYOUT) {
      let showTooltip;
      if (layout_ === LAYOUT.SMALL || layout_ === LAYOUT.SMALL_1_2) {
        // 设计师规定，卡片宽度较小时只显示三项tooltip字段
        showTooltip = tooltipArray.slice(0, 3);
      } else {
        // 其他情况下展示全量字段
        showTooltip = tooltipArray;
      }
      return showTooltip;
    }

    function showKLineTooltip(kLineIns: unknown, dataIndex_?: number | undefined) {
      const { defaultTooltipData: defaultKLineTooltip } = hook.getHXKLineTooltipData(
        kLineIns,
        'kline',
        Math.max(
          0,
          Math.min(
            (hxKLine.getDataList() as KLineData[]).findLastIndex(
              item => !Number.isNaN(+item.close)
            ),
            dataIndex_
          )
        )
      );
      const { LAYOUT } = util.const;
      const showTooltip = getTooltipData(defaultKLineTooltip, layout, LAYOUT);

      kLineTooltipIns.show(showTooltip);
    }

    kLineTooltipIns = new ui.KLineTooltip($tooltipContainer, {
      token
    });
    tooltipIns = new ui.Tooltip({
      container: $chart,
      formatter: dataIndex_ => {
        return hook.buildTooltip({
          config: hook.buildStandardChartPresetOption(api).tooltip,
          title: xAxisData[dataIndex_],
          items: y.map(item => {
            const formatResult = hook.formatDataNumberValue(
              drawLines.linesMap[item].data[dataIndex_],
              {
                type: 'table',
                unit: dataMetaInfo[item]?.unit
              }
            );

            return {
              type: util.const.LEGEND_TYPE.Line,
              color: drawLines.linesMap[item].color,
              name: item,
              value: formatResult.value + formatResult.unit
            };
          }),
          noWrapper: true
        });
      }
    });

    // * -------------------------- chart
    const TIMELINE_INTERVAL = 1e3;
    const [dataZoomStartIndex, dataZoomEndIndex] = [0, xAxisData.length - 1];

    // 初始化左侧Y轴
    const initLeftYAxis = ({ min, max, splitNum }) => {
      const yAxisDom = $leftYAxis;
      const range = max - min;
      const { height, width } = hxKLine.getSize('candle_pane', 'yAxis');

      yAxisDom.style.cssText += `height: ${height}px;width:fit-content;`;

      for (let i = 0; i < splitNum + 1; i++) {
        const yAxisVal = (range / splitNum) * i + min;
        const heightTemp = 100 / splitNum + (i % (splitNum - 1) !== 0 ? '%' : '% - 8px');
        const formatResult = hook.formatDataNumberValue(yAxisVal);

        yAxisDom.innerHTML += `<div style="height: calc(${heightTemp})">${formatResult.value} ${formatResult.unit}</div>`;
      }
    };

    // 更新左侧Y轴
    const updateLeftYAxis = ({ min, max, splitNum, gap }) => {
      let range = max - min;
      const gapValue = (range / (1 - gap * 2) - range) / 2;
      const YAsixDom = $leftYAxis;
      min -= gapValue;
      max += gapValue;
      range += gapValue * 2;
      const domList = YAsixDom.childNodes;

      for (let i = 0; i < splitNum + 1; i++) {
        const yAxisVal = (range / splitNum) * i + min;
        const formatResult = hook.formatDataNumberValue(yAxisVal);

        domList[splitNum - i].innerHTML = `${formatResult.value} ${formatResult.unit}`;
      }
    };

    // 渲染折线数据
    function renderLine(key: string) {
      hxKLine.zoomToShowAllData();
      hxKLine.setZoomEnabled(false);

      const gap = 0.1;
      const dataList = util.hxKLine
        .adaptData(
          hxKLine.getDataList() as KLineData[],
          drawLines.linesMap[key].data
            .slice(dataZoomStartIndex, dataZoomEndIndex + 1)
            .map((item, index) => ({
              timestamp: new Date(xAxisData[dataZoomStartIndex + index]).getTime(),
              dataIndex: dataZoomStartIndex + index,
              value: item
            }))
        )
        .map(item => ({
          ...item,
          value: item.__originData.value,
          dataIndex: item.__originData.dataIndex
        }));
      // 将模拟数据调整Y轴比例，缩放到图表中
      const yAxis = hxKLine.getYAxisRangeInfo();
      const yAxisMin = yAxis.min + yAxis.range * gap;
      const yAxisMax = yAxis.max - yAxis.range * gap;
      const yAxisRange = yAxisMax - yAxisMin;
      // 找出最大值和最小值
      let max = dataList[0].value;
      let min = dataList[0].value;
      dataList.forEach(item => {
        if (item.value > max) {
          max = item.value;
        }
        if (item.value < min) {
          min = item.value;
        }
      });

      // 比例尺同步
      const range = max - min;

      dataList.forEach(item => {
        item.value = yAxisMin + (yAxisRange * (item.value - min)) / range;
      });

      // 渲染折线
      hxKLine.createOverlay({
        name: 'line',
        points: dataList,
        extendData: {
          color: drawLines.linesMap[key].color,
          size: 1.5,
          data: dataList,
          enableTooltip: drawLines.yAxisKey === key,
          onMouseEnter: event => {
            if (event.dataIndex === '') {
              return;
            }

            tooltipIns.show([event.x, event.y], event.dataIndex);
          },
          onMouseLeave: () => {
            tooltipIns.hide();
          }
        }
      } as ExtendLineSeries);

      if (drawLines.yAxisKey === key) {
        // 确定可是区域内覆盖物数据的最大值和最小值，更新Y轴
        updateLeftYAxis({
          max,
          min,
          splitNum: yAxisSplitNum,
          gap: drawLines.gap
        });
      }
    }

    function renderHXKLine() {
      if (hxKLine) {
        HXKline.disposeChart(
          (hxKLine._chartContainer as HTMLElement).parentElement?.getAttribute('id'),
          (...args) => {
            console.log('hxKLine destroy', ...args);
          }
        );
      }

      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      hxKLine = HXKline.initChart(
        $kline.getAttribute('id'),
        /**
         * @see https://khtest.10jqka.com.cn/kodexplorer/data/User/wangyifan/home/HXKlineChart/docs/guide/datafeed-datasource.html
         */
        {
          id: util.common.generateUID(),
          code: selectedStock.code,
          market: selectedStock.market,
          data_class: 'kline',
          time_period: 'day_1',
          trade_date: [
            new Date(xAxisData[dataZoomStartIndex]),
            new Date(xAxisData[dataZoomEndIndex])
          ],
          adjust_type: 'forward'
        },
        /**
         * @see https://khtest.10jqka.com.cn/kodexplorer/data/User/wangyifan/home/HXKlineChart/docs/guide/chart-api.html
         */
        {
          chartType: '',
          dateFormat: 'YYYY-MM-DD',
          locale: 'zh_CN',
          styles: 'customTheme'
        },
        (...args) => {
          if ((args[0] as unknown as string) !== 'success') {
            return;
          }

          hxKLine.setZoomEnabled(false);
          hxKLine.setScrollEnabled(false);
          hxKLine.setBarSpaceLimit({ min: 0, max: 50 });
          hxKLine.zoomToShowAllData();
          hxKLine.setStyles(hxKLineAndDynamicLineToken.option);

          initLeftYAxis({
            min: 0,
            max: 100,
            splitNum: yAxisSplitNum
          });

          y.forEach(item => {
            renderLine(item);
          });

          // 处理 dom 相关的问题
          const $main = $kline.querySelector('div > div > div:nth-child(1)') as HTMLElement;
          const $xAxis = $kline.querySelector(
            'div > div:nth-child(2) > div:nth-child(1)'
          ) as HTMLElement;
          const $yAxis = $kline.querySelector('div > div > div:nth-child(2)') as HTMLElement;
          $xAxis.style.cursor = 'default';
          $yAxis.style.cursor = 'default';
          if ($main) {
            $main.style.borderLeft = borderLineStyle;
            $main.style.borderTop = borderLineStyle;
          }
          const $leftYAxisName = document.createElement('div');
          const $rightYAxisName = document.createElement('div');
          const $xAxisName = document.createElement('div');
          $leftYAxisName.style.cssText =
            'position: absolute; left: 4.5px; top: 4px;font-size: 12px; color: rgba(0, 0, 0, 0.45); z-index: 2;';
          $rightYAxisName.style.cssText =
            'position: absolute; right:4.5px; top: 4px;font-size: 12px; color: rgba(0, 0, 0, 0.45); z-index: 2;';
          $xAxisName.style.cssText =
            'position: absolute; right:4.5px; bottom: 4px;font-size: 12px; color: rgba(0, 0, 0, 0.45); z-index: 2;';
          $rightYAxisName.innerHTML = '股价(元)';
          $leftYAxisName.innerHTML =
            drawLines.yAxisKey + (drawLines.yAxisDataUnit ? `(${drawLines.yAxisDataUnit})` : '');
          $xAxisName.innerHTML = x;
          $main.appendChild($leftYAxisName);
          $main.appendChild($rightYAxisName);
          $main.appendChild($xAxisName);

          // eslint-disable-next-line @typescript-eslint/no-use-before-define, sonarjs/no-extra-arguments
          playByIndex(dateIndex, dateIndex);

          // 展示最新一期 tooltip
          showKLineTooltip(
            hxKLine,
            (hxKLine.getDataList() as KLineData[]).findLastIndex(item => !Number.isNaN(+item.close))
          );
        }
      );

      /**
       * - gap 限制主图内容的上下间隔
       * - scrollZoomEnabled 限制y轴的拖动
       */
      hxKLine.setPaneOptions({
        id: 'candle_pane',
        gap: {
          top: 0.05,
          bottom: 0.05
        },
        axisOptions: { scrollZoomEnabled: false }
      });

      /** 实现自定义 tooltip */
      hxKLine.subscribeAction('onCrosshairChange', (...args) => {
        if (!args[0].kLineData) {
          return;
        }

        showKLineTooltip(hxKLine, args[0].dataIndex);
      });
    }

    let klineDataList: KLineData[];
    function playByIndex(lastDateIndex: number, dateIndex_: number) {
      if (klinePlayTimer) {
        clearTimeout(klinePlayTimer);
      }

      klineDataList = klineDataList || (hxKLine.getDataList() as KLineData[]);
      const tempVal = klineDataList[0].close;
      let max = klineDataList[0].high;
      let min = klineDataList[0].low;
      const dataForPlay = klineDataList.map(item => {
        if (item.high > max) {
          max = item.high;
        }
        if (item.low < min) {
          min = item.low;
        }
        return {
          timestamp: item.timestamp,
          close: NaN,
          high: tempVal,
          low: tempVal,
          open: tempVal,
          volume: tempVal,
          turnover: tempVal
        };
      });
      dataForPlay.at(-1).high = max;
      dataForPlay.at(-1).low = min;
      dataForPlay.at(-1).open = min;

      // 1 正向播，-1 倒退
      const direction = lastDateIndex < dateIndex_ ? 1 : -1;
      let startTimestamp = new Date(xAxisData[lastDateIndex]).getTime();
      let endTimestamp = new Date(xAxisData[dateIndex_]).getTime();
      if (startTimestamp > endTimestamp) {
        [startTimestamp, endTimestamp] = [endTimestamp, startTimestamp];
      }

      const startIndex = util.hxKLine.findIndexByTimestamp(klineDataList, startTimestamp, 'start');
      const endIndex = util.hxKLine.findIndexByTimestamp(klineDataList, endTimestamp, 'end');

      function replaceData(index: number, endIndex_: number, time: number) {
        if (index === endIndex_) {
          // 展示最新一期数据
          showKLineTooltip(
            hxKLine,
            (hxKLine.getDataList() as KLineData[]).findLastIndex(item => !Number.isNaN(+item.close))
          );
          return;
        }

        const newDataList = [...klineDataList.slice(0, index + 1), ...dataForPlay.slice(index + 1)];

        hxKLine.applyNewData(newDataList);

        klinePlayTimer = setTimeout(() => {
          replaceData(index > endIndex_ ? index - 1 : index + 1, endIndex_, time);
        }, time);
      }

      if (direction === -1) {
        const newDataList = [
          ...klineDataList.slice(0, endIndex + 1),
          ...dataForPlay.slice(endIndex + 1)
        ];

        hxKLine.applyNewData(newDataList);

        replaceData(endIndex, startIndex + 1, TIMELINE_INTERVAL / Math.abs(endIndex - startIndex));
      } else {
        const newDataList = [
          ...klineDataList.slice(0, startIndex + 1),
          ...dataForPlay.slice(startIndex + 1)
        ];

        hxKLine.applyNewData(newDataList);

        replaceData(
          startIndex,
          // ! 为了把最后一个折线点也画出来
          endIndex + 2,
          TIMELINE_INTERVAL / Math.abs(endIndex - startIndex)
        );
      }
    }

    if (showTimeline) {
      const renderParams = {
        dataIndex: dateIndex,
        animation: {
          intervalTime: TIMELINE_INTERVAL
        },
        ...api.token?.dvTimeline?.config
      };
      api.hook.beforeRenderTimeline(api, renderParams);

      if (window.ThsDataVTimeline) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        timelineIns = new ThsDataVTimeline.Timeline($timeline, {
          theme: timelineTheme,
          data: xAxisData,
          config: renderParams
        });
      } else {
        console.warn('动态AIGC组件需要额外引入ThsDataVTimeline以支持时间轴功能');
      }
    }

    // * events ----------------------------
    legendIns.klineDropdown[0].on('change', event => {
      selectedStock = stocks.find(item => item.code === event.key);

      renderHXKLine();
    });

    timelineIns?.on?.('change', event => {
      const index = event.index as number;

      playByIndex(dateIndex, index);
      dateIndex = index;
    });
    timelineDropdownIns?.on?.('change', event => {
      const index = event.index as number;

      playByIndex(dateIndex, index);
      dateIndex = index;
    });

    renderHXKLine();

    return () => {
      clearTimeout(klinePlayTimer);
      legendIns?.destroy?.();
      kLineTooltipIns?.destroy?.();
      tooltipIns?.destroy?.();
      timelineIns?.destroy?.();
      timelineDropdownIns?.destroy?.();
      HXKline.disposeChart($kline, (...args) => {
        console.log('hxKLine destroy', ...args);
      });

      $dom.innerHTML = '';
    };
  }
});
